import React from 'react';
import { InfoCardVariants } from '@backstage/core-components';
import { EntityTable, useEntity } from '@backstage/plugin-catalog-react';
import { EntityRefLinkWithVuid } from '../../VuidChip';
import { Entity, RELATION_OWNER_OF } from '@backstage/catalog-model';
import {
  createEntityNameColumn,
  FILTER_TABLE_OPTIONS,
  FilterableColumn,
  orUnknown,
  RelatedEntitiesCard,
  UNKNOWN,
} from '../RelatedEntitiesCard/RelatedEntitiesCard';
import { platformTeamRef } from '../utils';

export interface HasProductsCardProps {
  variant?: InfoCardVariants;
}

function platformTeamOf(product: Entity): string {
  return (product.spec?.platformTeam as string | undefined) || UNKNOWN;
}

/**
 * Who runs the infrastructure behind each product. A team hands parts of its
 * portfolio to different platform teams, so the dropdown answers which products
 * sit with which of them.
 */
function createPlatformTeamColumn(): FilterableColumn<Entity> {
  const none = { value: UNKNOWN, label: 'No Platform Team' };
  return {
    title: 'Platform Team',
    // The vuid behind the link, so the column sorts and filters on the same
    // string the CMDB knows the team by.
    field: 'spec.platformTeam',
    filterValueOf: platformTeamOf,
    filterValueKind: 'group',
    filterValueNone: none,
    filterPlaceholder: 'All platform teams',
    render: product => {
      const team = platformTeamRef(product);
      if (!team) {
        return <span style={{ color: '#888' }}>{none.label}</span>;
      }
      return <EntityRefLinkWithVuid entityRef={team} defaultKind="group" />;
    },
  };
}

function managementModeOf(product: Entity): string {
  return (product.spec?.managementMode as string | undefined) || UNKNOWN;
}

/**
 * What the platform team does for a product: `full` for one whose
 * infrastructure it sets up and runs, `inventory` for one that is only on
 * record - see MANAGEMENT_MODES in the About card, which spells the two out.
 *
 * The value is shown as the CMDB writes it rather than as the sentence the
 * About card makes of it: a column is read down, and a table of sentences is
 * not. Next to the platform team, because it says what that team does here.
 */
function createManagementModeColumn(): FilterableColumn<Entity> {
  return {
    title: 'Setup',
    field: 'spec.managementMode',
    filterValueOf: managementModeOf,
    filterValueNone: { value: UNKNOWN, label: UNKNOWN },
    filterPlaceholder: 'All setups',
    render: product => orUnknown(managementModeOf(product)),
  };
}

export const HasProductsCard = (props: HasProductsCardProps) => {
  function relationType(entity: Entity): string {
    if (entity.kind === 'Group' && entity?.spec?.type === 'team') {
      return RELATION_OWNER_OF;
    }
    return 'deployedIn';
  }

  const { variant = 'gridItem' } = props;
  const { entity } = useEntity();

  const resourceEntityColumns: FilterableColumn<Entity>[] = [
    createEntityNameColumn<Entity>('product'),
    EntityTable.columns.createMetadataDescriptionColumn(),
    createPlatformTeamColumn(),
    createManagementModeColumn(),
  ];

  // A retired product is left out by the card itself, together with the wound
  // up teams and departments of every other table - see RelatedEntitiesCard.
  const asResourceEntities = (entities: Entity[]): Entity[] => entities;

  return (
    <RelatedEntitiesCard
      variant={variant}
      title="Products"
      entityKind="Product"
      relationType={relationType(entity)}
      columns={resourceEntityColumns}
      asRenderableEntities={asResourceEntities}
      countInTitle
      tableOptions={FILTER_TABLE_OPTIONS}
    />
  );
};
